import { useCallback, useState } from 'react';

import {
  type PositionListResponse,
  PositionListResponseSchema,
  type PositionTimelineResponse,
  PositionTimelineResponseSchema,
} from '../../api';
import type { ApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { type ApiQueryResult, useApiQuery } from '../../lib/api/use-api-query';
import { useAppVisible } from '../../lib/telegram/use-app-visible';

/** M7 R1, master X8, technical spec §4: `GET /positions` is refreshed every 5 s while E8 is visible. */
const POSITIONS_REFRESH_INTERVAL_MS = 5_000;

/**
 * What E8 renders (and E10 reads): the last list received (if any), how current it is, and the
 * failure of the first load or of the latest refresh.
 */
export interface PositionsQuery {
  /** Last successful `GET /positions`; kept when a later refresh fails (M5 §4 "Error", M7 R7). */
  readonly data: PositionListResponse | undefined;
  /**
   * When `data` arrived (device clock): freshness is judged on it (M7 R9), and so is the day a
   * lost position's "as of" time is compared with, without reading the clock while rendering.
   */
  readonly receivedAt: number | undefined;
  /**
   * Failure of the first load, when there is no list to show: the M5 error with Retry (M7 R7,
   * as Home's Load failed). `undefined` once a list arrived.
   */
  readonly error: ApiError | undefined;
  /**
   * Failure of the latest 5 s refresh while the last list stays on screen (M7 R7): shown by the
   * freshness banner (Offline or refused, M7 §4), retried at the next tick, never with Retry.
   */
  readonly refreshError: ApiError | undefined;
  /** A refresh is in flight while the list is on screen (M7 R5): the small activity mark. */
  readonly refreshing: boolean;
  /** The Retry button's load, after a failed first load. */
  readonly refetch: () => void;
  /** `true` from a Retry until its answer arrives: the button shows it is busy (M5 §4 "Error"). */
  readonly retrying: boolean;
}

/** The `refetch` of a query plus whether that load is still in flight. */
interface Retry {
  readonly retry: () => void;
  readonly retrying: boolean;
}

/** Marks "no Retry in flight": `undefined` is a real value (a query still pending). */
const IDLE = Symbol('idle');

/**
 * Tracks a Retry of the timeline (also used by M6): `useApiQuery` keeps the last answer on screen
 * while it loads again, and its `refreshing` flag is also true for a load the button did not start
 * (a new `load`), which must not make the button busy. Each answer (data, error or refresh error)
 * is a new object, so a Retry is in flight while the query still shows the answer it was clicked
 * on. Derived during render, no effect.
 */
function useRetry(answer: unknown, refetch: () => void): Retry {
  const [clickedOn, setClickedOn] = useState<unknown>(IDLE);
  const retry = useCallback(() => {
    setClickedOn(answer);
    refetch();
  }, [answer, refetch]);
  return { retry, retrying: clickedOn !== IDLE && clickedOn === answer };
}

/** Options of {@link usePositions}. */
interface UsePositionsOptions {
  /**
   * Opt-in polling, with no default (M7 R1–R4): `true` refreshes every 5 s while the app is
   * visible, `false` pauses the polling (E8 with its History tab on display, R4) and the list
   * loads at once when it turns `true` again (R3). A screen mounted with `false` sends nothing
   * until it turns `true` (`useApiQuery` starts not started): it is not a single read. Omitted: one read, never polled, which is what
   * E10 needs (M4 §5 "Read": the sheet reads once). Keep it either always set or always omitted
   * for one mounted screen: `useApiQuery` decides at mount whether the query is polled.
   */
  readonly poll?: boolean;
}

/**
 * Open positions from `GET /positions` (M5 §5), parsed with the contract schema. Two screens call
 * it: E8 polls it (`{ poll: tab === 'open' }`, M5 R15, M7 R1–R4), E10 reads it once (no option, M4
 * §5 "Read"). Polling is opt-in so a new caller never polls by accident.
 *
 * A failed refresh keeps the last list (`refreshError`, M7 R7); a first load that fails is
 * `error`, waits for Retry and is never polled (M7 R7); for a single read (E10), `error` is the
 * failure of that read. A Retry is the only load while the query is in error (polling starts after
 * a success), so `retrying` is simply "error and a load in flight". A 401 `TOKEN_EXPIRED` is
 * renewed once by the API client (S2 R18, M7 R8).
 */
export function usePositions({ poll }: UsePositionsOptions = {}): PositionsQuery {
  const api = useApiClient();
  const visible = useAppVisible();
  const load = useCallback(
    (signal: AbortSignal) => api.get('/positions', PositionListResponseSchema, signal),
    [api],
  );
  const query = useApiQuery(
    load,
    poll === undefined
      ? {}
      : { poll: { intervalMs: POSITIONS_REFRESH_INTERVAL_MS, active: visible && poll } },
  );
  return {
    data: query.data,
    receivedAt: query.receivedAt,
    error: query.error,
    refreshError: query.refreshError,
    refreshing: query.status === 'success' && query.refreshing,
    refetch: query.refetch,
    retrying: query.status === 'error' && query.refreshing,
  };
}

/**
 * Timeline of one position from `GET /positions/{positionId}/timeline` (spec R6). Called when a
 * card is expanded; the card unmounts it when collapsed, so reopening fetches again and an open
 * timeline is not polled (R15, proposal). M6 reuses it for closed positions.
 */
export function usePositionTimeline(
  positionId: string,
): ApiQueryResult<PositionTimelineResponse> & { readonly retrying: boolean } {
  const api = useApiClient();
  const load = useCallback(
    (signal: AbortSignal) =>
      api.get(
        `/positions/${encodeURIComponent(positionId)}/timeline`,
        PositionTimelineResponseSchema,
        signal,
      ),
    [api, positionId],
  );
  const query = useApiQuery(load);
  const { retry, retrying } = useRetry(
    query.refreshError ?? query.data ?? query.error,
    query.refetch,
  );
  return { ...query, refetch: retry, retrying };
}
